/** Fetch on open/focus and after writes; every mutation bumps the backend config version. */
import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { useFirebaseAuth } from 'context/FirebaseAuthContext';
import {
  RULES_COLLECTION,
  SETTINGS_COLLECTION,
  SETTINGS_DOC,
  SYSTEM_RULES
} from '../config/rateShopConstants';
import { validateRule } from '../lib/validateRule';
import { useRequestGuard, useRefreshOnVisible } from './useRequestGuard';

export const sortRules = rules =>
  [...rules].sort(
    (a, b) =>
      (a.priority ?? 1000) - (b.priority ?? 1000) ||
      String(a.id).localeCompare(String(b.id))
  );
export const PRIORITY_STEP = 10;
export const nextPriority = rules =>
  (rules.length ? Math.max(...rules.map(r => Number(r.priority) || 0)) : 0) +
  PRIORITY_STEP;
export const toRuleDoc = rule => ({
  name: String(rule.name || '').trim(),
  enabled: rule.enabled !== false,
  priority: Number(rule.priority),
  stopProcessing: Boolean(rule.stopProcessing),
  system: Boolean(rule.system),
  match: { all: rule.match?.all || [], any: rule.match?.any || [] },
  actions: rule.actions || [],
  notes: rule.notes || ''
});

export function useRateShopRules() {
  const [user, authLoading] = useFirebaseAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const guard = useRequestGuard();
  const refresh = useCallback(async () => {
    if (!guard.mounted()) return;
    const token = guard.begin();
    if (authLoading || !user) {
      setRules([]);
      setLoading(authLoading);
      setError(null);
      return;
    }
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, RULES_COLLECTION));
      if (!guard.current(token)) return;
      const result = sortRules(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setRules(result);
      setError(null);
      return result;
    } catch (err) {
      if (guard.current(token)) setError(err);
    } finally {
      if (guard.current(token)) setLoading(false);
    }
  }, [authLoading, user, guard]);
  useEffect(() => {
    refresh();
    return guard.invalidate;
  }, [refresh, guard]);
  useRefreshOnVisible(refresh);
  const stamp = useCallback(() => {
    if (!user?.email) throw new Error('Not signed in');
    return { updatedAt: serverTimestamp(), updatedBy: user.email };
  }, [user]);

  const saveRule = useCallback(
    async rule => {
      const body = toRuleDoc(rule);
      const { ok, errors } = validateRule(body);
      if (!ok) throw new Error(errors.join('\n'));
      const stamps = stamp();
      if (!rule.id && (await getDocs(collection(db, RULES_COLLECTION))).empty)
        throw new Error('Seed the system safety rules before creating a rule.');
      const ref = rule.id
        ? doc(db, RULES_COLLECTION, rule.id)
        : doc(collection(db, RULES_COLLECTION));
      await setDoc(ref, { ...body, ...stamps });
      await refresh();
      return ref.id;
    },
    [stamp, refresh]
  );
  const setRuleEnabled = useCallback(
    async (id, enabled) => {
      await updateDoc(doc(db, RULES_COLLECTION, id), {
        enabled: Boolean(enabled),
        ...stamp()
      });
      await refresh();
    },
    [stamp, refresh]
  );
  const deleteRule = useCallback(
    async id => {
      const stamps = stamp();
      const current = await getDocs(collection(db, RULES_COLLECTION));
      if (current.size <= 1)
        throw new Error(
          'The last remaining rule cannot be deleted. Disable it instead.'
        );
      const batch = writeBatch(db);
      batch.delete(doc(db, RULES_COLLECTION, id));
      batch.set(doc(db, SETTINGS_COLLECTION, SETTINGS_DOC), stamps, {
        merge: true
      });
      await batch.commit();
      await refresh();
    },
    [stamp, refresh]
  );
  const reorderRules = useCallback(
    async orderedIds => {
      const stamps = stamp();
      const batch = writeBatch(db);
      orderedIds.forEach((id, i) =>
        batch.update(doc(db, RULES_COLLECTION, id), {
          priority: (i + 1) * PRIORITY_STEP,
          ...stamps
        })
      );
      await batch.commit();
      await refresh();
    },
    [stamp, refresh]
  );
  const seedSystemRules = useCallback(async () => {
    const stamps = stamp();
    const current = await getDocs(collection(db, RULES_COLLECTION));
    if (!current.empty) {
      await refresh();
      throw new Error(
        'Rules already exist. Refreshed the list; no rules were overwritten.'
      );
    }
    const batch = writeBatch(db);
    SYSTEM_RULES.forEach(rule =>
      batch.set(doc(db, RULES_COLLECTION, rule.id), {
        ...toRuleDoc(rule),
        ...stamps
      })
    );
    await batch.commit();
    await refresh();
  }, [stamp, refresh]);
  return {
    rules,
    loading,
    error,
    refresh,
    saveRule,
    setRuleEnabled,
    deleteRule,
    reorderRules,
    seedSystemRules,
    nextPriority: nextPriority(rules)
  };
}
